'use client';

import './gallery-main.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.ts';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselNext,
  CarouselPrevious,
} from '../../../molecules/carousel/carousel.tsx';
import { FeatureCard } from '../../../molecules/feature-card/feature-card.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import {
  Lightbox,
  LightboxTrigger,
} from '../../../molecules/lightbox/lightbox.tsx';
import { getPlaceholderItems } from '../../../utils/storybook.tsx';
import { getBaseSizeProps } from '../../../utils/text-utils.tsx';

export const GalleryMain: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  items,
  ...props
}) => {
  const { t } = useTranslation(['skippy']);

  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image;
  const hasHeaderGroup = hasHeaderContent && !media?.image;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const galleryItems =
    !items || items.length < 3 ? getPlaceholderItems(items, 3) : items;

  return (
    <section className="GalleryMain component">
      <div className="GalleryMain__container container flex flex-col gap-comfy-xl lg:gap-roomy-xs">
        {hasHeaderCard && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            inline
          />
        )}
        {hasHeaderGroup && <HeaderGroup {...props} seoLevel={seoLevel} />}

        <Carousel
          aria-label={t('skippy:gallery.title', {
            title: props.title,
          })}
          opts={{
            align: 'start',
            loop: false,
            watchFocus: false,
          }}
        >
          <div className="GalleryMain__wrapper flex flex-col gap-comfy-lg">
            <div className="GalleryMain__carousel">
              <CarouselContent>
                {galleryItems.map((slide, index) => (
                  <CarouselItem
                    key={['slide', index, slide.title].join('-')}
                    index={index}
                    className="GalleryMain__slide"
                    aria-label={`${t('skippy:gallery.slide.title', {
                      index: index + 1,
                      length: galleryItems.length,
                    })}${slide.title && ` - ${slide.title}`}`}
                  >
                    <figure className="m-none">
                      <Lightbox
                        key={['lightbox', index, slide.title].join('-')}
                        items={galleryItems}
                        startIndex={index}
                      >
                        <LightboxTrigger
                          className="Lightbox__trigger w-full"
                          ariaLabel={t('skippy:gallery.open')}
                        >
                          <div className="GalleryMain__image">
                            <CarouselLazyLoadImage
                              index={index}
                              media={slide.media}
                              isVideoThumbnail={!!slide.media?.video}
                            />
                          </div>
                        </LightboxTrigger>
                      </Lightbox>
                      {slide.media?.caption && (
                        <figcaption className="GalleryMain__attribution label label-lg mt-comfy-sm">
                          {slide.media.caption || slide.title}
                        </figcaption>
                      )}
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>
            <div className="GalleryMain__arrows flex gap-comfy-sm items-center justify-end">
              <CarouselPrevious />
              <CarouselNext />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
};
